/*
 * Starts the web app: the font, the tokens and parts, the data cache and the pages.
 */
import "@fontsource-variable/inter";
import "./styles/tokens.css";
import "./styles/app.css";
import "./styles/phone.css";
import "./styles/books.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ApiError } from "./api/client";
import { signInLookQuery } from "./api/hooks";
import { router } from "./app/router";
import { fitWideScreens } from "./app/fit-wide";
import { setTabIcon } from "./app/tab-icon";
import { setHomeIcon } from "./app/home-icon";
import { startTooltipWarmup } from "./app/tips";
import { ToastProvider } from "./components/overlays";

try {
  const theme = localStorage.getItem("steward.theme");
  if (theme === "light" || theme === "dark") document.documentElement.dataset.theme = theme;
} catch {
  // No saved look; follow the device.
}

startTooltipWarmup();
fitWideScreens();

// A deploy swaps the server for 10 to 60 seconds (owner, Nov 5: "why does this happen so often?"). Loads that hit that
// gap (no answer, 502, 503, 504) keep trying for about 30 seconds before showing an error; anything else tries once more.
const SWAP_GAP = new Set([0, 502, 503, 504]);
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failures, error) => (error instanceof ApiError && SWAP_GAP.has(error.status) ? failures < 6 : failures < 1),
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    },
  },
});

// The Steward mark in this address's brand colors on the tab; the sign-in page reuses the same answer.
setTabIcon();
setHomeIcon();
queryClient
  .query(signInLookQuery)
  .then((look) => {
    if (look.brand) setTabIcon(look.brand.mark);
  })
  .catch(() => {
    // No brand here, or the server is away; the plain mark stays.
  });

const root = document.getElementById("root");
if (root) {
  createRoot(root).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}
